import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { LinearProgress, Box, Typography, Button, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import Question from './Question';
import { useTheme } from "@mui/material/styles";
import LoadingIcon from '../Misc/LoadingIcon';
import { loadQuestionnaire } from '../../redux/slices/onboarding/onboardingThunk';

const QuestionnaireSection = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const theme = useTheme();
    const onMobile = !useMediaQuery('(min-width:600px)');

    const { 
        questionnaire, 
        questionsLoading, 
        questionsLoadingError, 
        previousAnswers,
    } = useSelector((state) => state.onboarding);
    const { personInfo } = useSelector(state => state.physician);

    const [pageIndex, setPageIndex] = useState(parseInt(params.index));

    const [errorMessageOpen, setErrorMessageOpen] = useState(false);

    useEffect(() => {
        const asyncInitalizeQuestionnaire = async () => {
            dispatch(loadQuestionnaire());
            console.log(previousAnswers)
            console.log(questionnaire)
        }
        asyncInitalizeQuestionnaire();
        checkError();
    }, [dispatch, questionsLoadingError, personInfo]);

    const next = (e) => {
        e.preventDefault();
        ReactGA.event({
            category: 'Button',
            action: 'Click',
            label: `Question ${parseInt(pageIndex) + 1} Next`,
        });

        if(parseInt(pageIndex) === 0) {
            setPageIndex(parseInt(pageIndex) + 1);
            navigate(`/onboard/questionnaire/${parseInt(pageIndex) + 1}`);
        } else if(parseInt(pageIndex) + 1 < questionnaire.length) {
            setPageIndex(parseInt(pageIndex) + 1);
            navigate(`/onboard/questionnaire/${parseInt(pageIndex) + 1}`);
        } else {
            navigate('/home');
        }
    }

    const back = (e) => {
        e.preventDefault();
        ReactGA.event({
            category: 'Button',
            action: 'Click',
            label: `Question ${parseInt(pageIndex) + 1} Previous`,
        });

        if(parseInt(pageIndex) === 1) {
            setPageIndex(parseInt(pageIndex) - 1);
            navigate(`/onboard/questionnaire/${parseInt(pageIndex) - 1}`);
        } else if(parseInt(pageIndex) - 1 >= 0) {
            setPageIndex(parseInt(pageIndex) - 1);
            navigate(`/onboard/questionnaire/${parseInt(pageIndex) - 1}`);
        } else {
            navigate('/home');
        }
    }

    const checkError = () => {
        if(questionsLoadingError === true) setErrorMessageOpen(true);
    }

    const onCloseDialog = () => {
        setErrorMessageOpen(false);
        navigate('/home');
    }

    const progressBarValue = (parseInt(pageIndex)) / questionnaire.length * 100;
    
    return (
        <>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                paddingTop: !onMobile ? "4rem" : "4rem",
                overflow: "hidden",
            }}>
                <Box sx={{ position: "fixed", top: "4rem" }}>
                    <LinearProgress 
                        variant="determinate"
                        value={progressBarValue} 
                        sx={{
                            height: "0.75rem",
                            minWidth: "100vw",
                            backgroundColor: theme.palette.caralystNavy.light,
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: theme.palette.caralystNavy.main,
                            },
                    }}/>
                    <Typography
                        sx={{
                            position: "absolute",
                            left: progressBarValue === 100 ? (progressBarValue - 5)  + "%" :
                                    progressBarValue === 0 ? (progressBarValue + 0.5)  + "%" :
                                        (progressBarValue - 0.5) + "%",
                            top: "1rem",
                        }}
                    >
                        {Math.round(progressBarValue)}%
                    </Typography>
                </Box>
                <Box aria-label='question-wrapper' sx={{
                    marginTop: "1rem",
                    overflow: "scroll",
                    marginBottom: "5rem",
                    width: "95vw",
                    maxWidth: "80rem",
                    scrollbarWidth: "none",
                }}>
                    {(questionsLoading) ?
                        <Box sx={{marginTop: "10rem"}}>
                            <LoadingIcon textInfo="Loading Quiz..."/>
                        </Box>
                        :
                        questionnaire?.length && questionnaire.length > 0 && questionnaire[pageIndex].SurveyQuestions.map((question, qInd) => {
                            return (
                                <Question
                                    pInd={pageIndex}
                                    question={question}
                                    qInd={qInd}
                                    next={next}
                                />
                            )
                        })
                    }
                </Box>
                <Box sx={{
                    width: "100%",
                    position: "fixed",
                    bottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <Button 
                        onClick={back}
                        sx={{
                            backgroundColor: theme.palette.caralystNavy.main,
                            color: "white",
                            transition: "all 0.1s ease-in-out",
                            margin: "0.5rem",
                            width: "8rem",
                            '&:hover': {
                                backgroundColor: theme.palette.caralystNavy.main,
                                filter: "brightness(150%)"
                            }
                    }}>
                        Previous
                    </Button>
                    <Button 
                        onClick={next}
                        sx={{
                            backgroundColor: theme.palette.caralystNavy.main,
                            color: "white",
                            transition: "all 0.1s ease-in-out",
                            margin: "0.5rem",
                            width: "8rem",
                            '&:hover': {
                                backgroundColor: theme.palette.caralystNavy.main,
                                filter: "brightness(150%)"
                            }
                    }}>
                        Next
                    </Button>
                </Box>
                <Box sx={{
                    backgroundColor: theme.palette.caralystNavy.light,
                    position: "fixed",
                    top: "100%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    minWidth: "120rem",
                    width: "120%",
                    height: "100%",
                    marginBottom: "5rem",
                    borderRadius: "50% 50%",
                    opacity: "0.4",
                    zIndex: "-1",
                }}/>
            </Box>
             <Dialog open={errorMessageOpen} onClose={onCloseDialog}>
                <DialogTitle>
                    <Typography variant='h4' fontWeight={500}>
                        Sorry!
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant='h6' fontWeight={400}>
                        We had an issue loading the questionnaire. Please try again. If this issue persists, please contact us at eng@caralyst.io!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseDialog} autoFocus>
                        Ok!
                    </Button>
                </DialogActions>
             </Dialog>
        </>
    )
}

export default QuestionnaireSection;